/**
 * Sizing a marker by how big it should look, not by how big it is.
 *
 * An axis drawn at a fixed length in metres is the wrong size twice over:
 * zoomed out to see the whole robot it collapses into the mesh, and zoomed
 * in on one joint it flies off the screen. A marker is an annotation, so it
 * wants a constant size on screen and a world size that follows the camera.
 *
 * This is the conversion that gives it one: how many metres a pixel spans
 * at a given distance from a perspective camera.
 */

/**
 * World units one pixel covers at `distance` from the camera.
 *
 * Parameters
 * ----------
 * distance
 *     Distance from the camera to the point being annotated, in world units.
 * fovDeg
 *     The camera's vertical field of view, in degrees.
 * viewportHeightPx
 *     Height of the rendered viewport, in pixels.
 *
 * Notes
 * -----
 * The visible height at a distance `d` is `2 d tan(fov/2)`; dividing by the
 * viewport height in pixels gives the world size of one pixel there.
 */
export function worldUnitsPerPixel(distance: number, fovDeg: number, viewportHeightPx: number): number {
  if (viewportHeightPx <= 0) {
    return 0;
  }
  const halfFov = (fovDeg * Math.PI) / 360;
  return (2 * Math.abs(distance) * Math.tan(halfFov)) / viewportHeightPx;
}

/**
 * The scale factor a unit-sized marker needs to cover `sizePx` pixels.
 *
 * Clamped away from zero so a marker at the camera's own position -- which
 * happens while the camera flies -- does not collapse to nothing and
 * disappear for a frame.
 */
export function screenSpaceScale(
  distance: number,
  fovDeg: number,
  viewportHeightPx: number,
  sizePx: number,
  minimum = 1e-5
): number {
  return Math.max(worldUnitsPerPixel(distance, fovDeg, viewportHeightPx) * sizePx, minimum);
}
